import { assetKindFromPath } from "@pax/api-contract/assets/v1alpha1";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { AssetNotFound, AssetScreen } from "@/features/assets/components/AssetScreen";
import { titleOf } from "@/features/assets/media";
import { assetBySlugQuery, cacheAssetResponse } from "@/features/assets/queries";
import { sessionQuery } from "@/features/auth/queries";
import { isNotFound } from "@/lib/api";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/assets/$kind/$slug/")({
  validateSearch: (search: Record<string, unknown>): { version?: string } =>
    typeof search.version === "string" && UUID.test(search.version)
      ? { version: search.version }
      : {},
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context: { queryClient }, params, deps: { version } }) => {
    const kind = assetKindFromPath(params.kind);
    if (!kind) throw notFound();
    const session = await queryClient.ensureQueryData(sessionQuery);
    const viewer = session?.user.id ?? null;
    const response = await queryClient
      .ensureQueryData(assetBySlugQuery(kind, params.slug, viewer, version))
      .catch(error => {
        if (isNotFound(error)) throw notFound();
        throw error;
      });
    cacheAssetResponse(queryClient, viewer, response, version);
    return { asset: response.asset };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData ? `${titleOf(loaderData.asset)} · Pax.co` : "Asset · Pax.co" }],
  }),
  component: AssetPage,
  notFoundComponent: AssetNotFound,
});

function AssetPage() {
  const { asset } = Route.useLoaderData();
  const { version } = Route.useSearch();
  return (
    <AssetScreen
      key={`${asset.id}:${version ?? ""}`}
      assetId={asset.id}
      versionId={version}
      known={asset}
    />
  );
}
